import React, { useMemo } from 'react'
import { Spinner } from 'react-bootstrap'

import './Tasks.scss'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectTaskPollingEnabled } from '../../../../../store/task/task'
import { selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { NewFormTask, UserGroupTasks } from '../../../../../views/Tasks/Tasks'
import { Task } from '../../../../../models/MeetingSupport'
import { User } from '../../../../../models/User'
import { addToast } from '../../../../../store/toast/toastSlice'
import { useTaskSelectionHandler } from '../../common/hooks/useTaskSelectionHandler'

export interface TasksProps {
  groupedTasks: { tasks: Task[]; user: User }[]
  isLoading: boolean
}

const EmptyState: React.FC = () => {
  return (
    <div className='bg-white border rounded-3 p-3'>
      <div className='d-flex align-items-center justify-content-center'>
        <h5 className='mb-0'>All caught up! No open tasks with these participants.</h5>
      </div>
    </div>
  )
}

export const Tasks: React.FC<TasksProps> = ({ groupedTasks, isLoading }) => {
  const roomSession = useAppSelector(selectRoomSession)
  const isPolling = useAppSelector(selectTaskPollingEnabled)
  const dispatch = useAppDispatch()

  const { selectionHandlers } = useTaskSelectionHandler()

  const contactsForMeeting = useMemo(() => roomSession?.users.filter((e) => e.type === 'external') ?? [], [roomSession?.users])

  const defaultRelatedToOptions = roomSession?.users ?? []
  const initialRelatedToUser = contactsForMeeting.length === 1 ? contactsForMeeting[0] : null

  if (!roomSession) {
    return null
  }

  const { roomId, id: sessionId } = roomSession

  const formTaskToCreateTaskPayload = (assignedToUserId: string, formTask: NewFormTask) => {
    const { subject, description, relatedToUserId } = formTask
    return {
      ...(relatedToUserId ? { relatedTo: { type: 'user', id: relatedToUserId } } : {}),
      ...(description ? { description } : {}),
      assignedTo: { type: 'user', id: assignedToUserId },
      task: subject,
      type: 'meeting',
      roomId,
      sessionId,
    }
  }

  const handleCopyTasks = () => {
    const tasksString = groupedTasks
      .map(({ user, tasks }) => {
        return `${user.name}: \n${tasks
          .map((task) => {
            const { task: subject, description } = task
            const descriptionPart = description ? `: ${description}` : ''
            return `\t- ${subject}${descriptionPart}`
          })
          .join('\n')}\n`
      })
      .join('')

    navigator.clipboard
      .writeText(tasksString)
      .then(() => {
        dispatch(addToast({ variant: 'success', title: 'Copied', messages: ['Tasks copied to the clipboard'] }))
      })
      .catch((err) => {
        console.error('Failed to copy tasks to clipboard:', err)
        dispatch(addToast({ variant: 'error', title: 'Copy failed', messages: ['Failed to copy tasks to clipboard'] }))
      })
  }

  if (isLoading || isPolling) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner />
      </div>
    )
  }

  if (groupedTasks.length === 0) {
    return <EmptyState />
  }

  return (
    <div className='bg-white border rounded-3 p-3 pb-4'>
      <div className='d-flex align-items-center justify-content-between mb-3 ps-3'>
        <h5 className='mb-0'>Open tasks from previous meetings</h5>
        <button className='btn btn-outline-secondary btn-sm d-flex align-items-center gap-2 paragraph-small bold text-dark' onClick={handleCopyTasks}>
          <i className='bi bi-files' />
          Copy all
        </button>
      </div>
      <div className='d-flex flex-column gap-2'>
        {groupedTasks.map(({ user, tasks }, index) => (
          <div key={user.id} className='bg-white border rounded-3 p-3 pb-4'>
            <UserGroupTasks
              user={user}
              tasks={tasks}
              row={index}
              formTaskToCreateTaskPayload={formTaskToCreateTaskPayload}
              defaultRelatedToOptions={defaultRelatedToOptions}
              initialRelatedToUser={initialRelatedToUser}
              selectionHandlers={selectionHandlers}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
